import { Router } from "express";

import { attendanceController } from "../controllers/attendance-controller";
import { attendanceReportController } from "../controllers/attendance-report-controller";
import { courseMaterialController } from "../controllers/course-material-controller";
import { courseModuleController } from "../controllers/course-module-controller";
import { teacherMobileController } from "../controllers/mobile/teacher-mobile-controller";
import { progressReportController } from "../controllers/progress-report-controller";
import { scheduleChangeController } from "../controllers/schedule-change-controller";
import { teacherController } from "../controllers/teacher-controller";
import { teacherReportsController } from "../controllers/teacher-reports-controller";
import {
  authenticate,
  createRateLimit,
  requireEmailVerification,
} from "../middleware/auth-middleware";
import { requireRole } from "../middleware/role-middleware";
import { uploadMiddleware } from "../middleware/upload-middleware";

const router = Router();

// Environment-aware rate limiting
const generalRateLimit = createRateLimit.general();

// Apply authentication and teacher role requirement to all routes
router.use(authenticate);
router.use(requireEmailVerification);
router.use(requireRole(["teacher"]));

/**
 * TEACHER-SPECIFIC ROUTES (for teachers to access their own data)
 */

/**
 * @route   GET /api/v1/teacher/classes
 * @desc    Get all classes for the authenticated teacher
 * @access  Private (Teacher)
 * @query   page, limit, sortBy, sortOrder, search, status, isActive, semesterId, subject
 */
router.get("/classes", generalRateLimit, (req, res) =>
  teacherController.getTeacherClasses(req, res),
);

/**
 * @route   GET /api/v1/teacher/classes/:id
 * @desc    Get class by ID with complete details (timings, location, enrolled students)
 * @access  Private (Teacher)
 */
router.get("/classes/:id", generalRateLimit, (req, res) =>
  teacherController.getTeacherClassById(req, res),
);

/**
 * @route   GET /api/v1/teacher/classes/:id/students
 * @desc    Get all enrolled students for a class
 * @access  Private (Teacher)
 * @query   page, limit, search
 */
router.get("/classes/:id/students", generalRateLimit, (req, res) =>
  teacherController.getTeacherClassEnrolledStudents(req, res),
);

/**
 * @route   GET /api/v1/teacher/classes/:classId/students/:id/attendance
 * @desc    Get attendance history for a student in a class
 * @access  Private (Teacher)
 * @query   page, limit
 */
router.get(
  "/classes/:classId/students/:id/attendance",
  generalRateLimit,
  (req, res) =>
    attendanceController.getStudentClassAttendanceForTeacher(req as any, res),
);

/**
 * @route   GET /api/v1/teacher/classes/:classId/students/:id
 * @desc    Get student details inside a specific class
 * @access  Private (Teacher)
 */
router.get("/classes/:classId/students/:id", generalRateLimit, (req, res) =>
  teacherController.getStudentInClassById(req as any, res),
);

/**
 * @route   PUT /api/v1/teacher/classes/:id/schedule
 * @desc    Update class schedule/timings (Teacher must be assigned)
 * @access  Private (Teacher)
 * @body    { schedule: [{ day, startTime, endTime, isActive }], restRoomMinutes?, finalAttendanceTime? }
 */
router.put("/classes/:id/schedule", generalRateLimit, (req, res) =>
  teacherController.updateClassSchedule(req, res),
);

/**
 * COURSE MATERIALS ROUTES
 */

/**
 * @route   GET /api/v1/teacher/classes/:classId/materials
 * @desc    Get all course materials for a class
 * @access  Private (Teacher)
 * @query   status, page, limit
 */
router.get("/classes/:classId/materials", generalRateLimit, (req, res) =>
  courseMaterialController.getCourseMaterials(req as any, res),
);

/**
 * @route   GET /api/v1/teacher/classes/:classId/materials/:id
 * @desc    Get course material by ID
 * @access  Private (Teacher)
 */
router.get("/classes/:classId/materials/:id", generalRateLimit, (req, res) =>
  courseMaterialController.getCourseMaterialById(req as any, res),
);

/**
 * @route   POST /api/v1/teacher/classes/:classId/materials
 * @desc    Create a new course material
 * @access  Private (Teacher)
 * @body    { title, description?, deadline?, order?, documents? }
 */
router.post(
  "/classes/:classId/materials",
  uploadMiddleware.array("materials", 10),
  generalRateLimit,
  (req, res) => courseMaterialController.createCourseMaterial(req as any, res),
);

/**
 * @route   PUT /api/v1/teacher/classes/:classId/materials/:id
 * @desc    Update course material
 * @access  Private (Teacher)
 * @body    { title?, description?, deadline?, order?, status?, documents? }
 */
router.put(
  "/classes/:classId/materials/:id",
  uploadMiddleware.array("materials", 10),
  generalRateLimit,
  (req, res) => courseMaterialController.updateCourseMaterial(req as any, res),
);

/**
 * @route   DELETE /api/v1/teacher/classes/:classId/materials/:id
 * @desc    Delete course material
 * @access  Private (Teacher)
 */
router.delete("/classes/:classId/materials/:id", generalRateLimit, (req, res) =>
  courseMaterialController.deleteCourseMaterial(req as any, res),
);

/**
 * ATTENDANCE ROUTES (Teacher)
 */

/**
 * @route   GET /api/v1/teacher/classes/:classId/attendance
 * @desc    Get students with attendance for a specific date
 * @access  Private (Teacher)
 * @query   date (required, YYYY-MM-DD)
 */
router.get("/classes/:classId/attendance", generalRateLimit, (req, res) =>
  teacherMobileController.getClassAttendance(req, res),
);

/**
 * @route   POST /api/v1/teacher/classes/:classId/attendance
 * @desc    Mark attendance for a class for a specific date
 * @access  Private (Teacher)
 * @body    { date: string, attendance: [{ studentId, status, remarks? }] }
 */
router.post("/classes/:classId/attendance", generalRateLimit, (req, res) =>
  teacherMobileController.markClassAttendance(req, res),
);

/**
 * @route   PUT /api/v1/teacher/classes/:classId/attendance/:studentId
 * @desc    Update individual student attendance
 * @access  Private (Teacher)
 * @body    { status: string, remarks?: string, date?: string }
 */
router.put(
  "/classes/:classId/attendance/:studentId",
  generalRateLimit,
  (req, res) => teacherMobileController.updateStudentAttendance(req, res),
);

router.get(
  "/classes/:classId/attendance/summary",
  generalRateLimit,
  (req, res) =>
    attendanceReportController.getTeacherClassAttendanceSummary(req, res),
);

router.get(
  "/classes/:classId/attendance/breaks-report",
  generalRateLimit,
  (req, res) => attendanceReportController.getClassBreaksReport(req, res),
);

router.get(
  "/classes/:classId/attendance/audit-log",
  generalRateLimit,
  (req, res) => attendanceReportController.getClassAttendanceAuditLog(req, res),
);

router.get("/reports/overview", generalRateLimit, (req, res) =>
  teacherReportsController.getOverview(req, res),
);
router.get("/reports/grades", generalRateLimit, (req, res) =>
  teacherReportsController.getGrades(req, res),
);
router.get("/reports/late-submissions", generalRateLimit, (req, res) =>
  teacherReportsController.getLateSubmissions(req, res),
);
router.get("/reports/attendance", generalRateLimit, (req, res) =>
  teacherReportsController.getAttendanceReport(req, res),
);

router.post("/courses/proposals", generalRateLimit, (req, res) =>
  courseModuleController.createCourseProposal(req, res),
);
router.get("/courses", generalRateLimit, (req, res) =>
  courseModuleController.getTeacherCourses(req, res),
);
router.get("/courses/:courseId", generalRateLimit, (req, res) =>
  courseModuleController.getTeacherCourseById(req, res),
);

router.post(
  "/classes/:classId/schedule-change-requests",
  generalRateLimit,
  (req, res) => scheduleChangeController.createScheduleChangeRequest(req, res),
);
router.get("/schedule-change-requests", generalRateLimit, (req, res) =>
  scheduleChangeController.getTeacherScheduleChangeRequests(req, res),
);

/**
 * @route   GET /api/v1/teacher/students/:studentId/progress-report
 * @desc    Get student progress report (Teacher view)
 * @access  Private (Teacher)
 * @query   classId, semesterId
 */
router.get(
  "/students/:studentId/progress-report",
  generalRateLimit,
  (req, res) =>
    progressReportController.getStudentProgressReportForTeacher(req, res),
);

export default router;
